import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { fetchOperationsKPIs, fetchOperationPlans, fetchAlerts } from './operationsSlice'
import { OperationsTabBar } from './components/OperationsTabBar'
import { KPICard } from './components/KPICard'
import { PlanCard } from './components/PlanCard'
import { AlertCard } from './components/AlertCard'
import {
  OpsPageWrapper,
  KPIStrip,
  OpsGrid,
  OpsCard,
  OpsCardHeader,
  opsColors,
} from './styled/Operations.styled'
import { NavBar } from 'components/NavBar/NavBar'

// Simple inline SVG icons for KPI cards
const RunningIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 2.05v2.02c3.95.49 7 3.85 7 7.93 0 1.55-.44 3-1.21 4.22l1.45 1.45A9.96 9.96 0 0022 12c0-5.18-3.95-9.45-9-9.95zM12 20c-4.42 0-8-3.58-8-8 0-3.55 2.31-6.56 5.5-7.6v-2.1C5.06 3.45 2 7.27 2 12c0 5.52 4.48 10 10 10 2.25 0 4.33-.74 6.01-2L16.55 18.5A7.96 7.96 0 0112 20z" /><path d="M12 6v6l4.24 2.54.76-1.27-3.5-2.09V6h-1.5z" /></svg>
)
const BlockedIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.902 7.902 0 014 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.902 7.902 0 0120 12c0 4.42-3.58 8-8 8z" /></svg>
)
const QueueIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" /></svg>
)
const WorkersIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>
)
const MachineIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65A.488.488 0 0014 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" /></svg>
)

export const OperationsHome: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { kpis, plans, alerts } = useSelector((state: RootState) => state.operations)

  useEffect(() => {
    dispatch(fetchOperationsKPIs())
    dispatch(fetchOperationPlans({}))
    dispatch(fetchAlerts(undefined))
  }, [dispatch])

  return (
    <>
      <NavBar />
      <OpsPageWrapper>
        <div>
          <h1 style={{ fontSize: '30px', fontWeight: 900, color: opsColors.textPrimary, margin: '0 0 0.5rem 0', fontFamily: 'DM Sans, sans-serif' }}>
            Operations Control
          </h1>
        </div>

        <OperationsTabBar />

        {/* KPI Strip */}
        {kpis && (
          <KPIStrip>
            <KPICard icon={<RunningIcon />} value={kpis.runningTasks} label="Running Tasks" color={opsColors.successBase} />
            <KPICard icon={<BlockedIcon />} value={kpis.blockedTasks} label="Blocked Tasks" color={opsColors.dangerBase} />
            <KPICard icon={<QueueIcon />} value={kpis.inQueue} label="In Queue" color={opsColors.brand500} />
            <KPICard icon={<WorkersIcon />} value={kpis.activeWorkers} label="Active Workers" color={opsColors.infoBase} />
            <KPICard icon={<MachineIcon />} value={kpis.activeMachines} label="Active Machines" color={opsColors.successBase} />
          </KPIStrip>
        )}

        {/* Main Content: Plans + Alerts */}
        <OpsGrid columns="65fr 35fr" gap="1.25rem">
          {/* Active Plans */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <OpsCardHeader>
              <h3>Active Operation Plans</h3>
            </OpsCardHeader>
            {plans?.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </div>

          {/* Alerts */}
          <OpsCard>
            <OpsCardHeader>
              <h3>Operational Alerts</h3>
            </OpsCardHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {alerts?.map((alert) => (
                <AlertCard key={alert.id} alert={alert} />
              ))}
            </div>
          </OpsCard>
        </OpsGrid>
      </OpsPageWrapper>
    </>
  )
}
